import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Shield, Zap, ArrowRight, CheckCircle2, MapPin, FileCheck } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsTrucking({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-[#0F172A] text-white font-sans" data-id="element-1991">
      <SimonsHeader activePage="trucking" setActivePage={setActivePage} data-id="element-1992" />

      {/* Hero Section */}
      <section className="py-32 px-8 relative overflow-hidden" data-id="element-1993">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-20 items-center relative z-10" data-id="element-1994">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-1995">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/10 border border-[#FFD700]/20 rounded-full text-[#FFD700] text-[10px] font-black uppercase tracking-widest mb-8" data-id="element-1996">
              Operational Continuity Guaranteed
            </div>
            <h1 className="text-6xl lg:text-8xl font-black mb-8 leading-[0.9] tracking-tighter" data-id="element-1997">
              Keep Your <br data-id="element-1998" />
              <span className="text-[#D32F2F]" data-id="element-1999">Fleet Moving.</span>
            </h1>
            <p className="text-xl text-white/60 mb-10 max-w-xl font-medium leading-relaxed" data-id="element-2000">
              Fast-track compliance and insurance for the logistics industry. We
              understand that every hour off the road is revenue lost.
            </p>
            <div className="flex flex-wrap gap-6" data-id="element-2001">
              <div className="flex items-center gap-3" data-id="element-2002">
                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-[#FFD700] border border-white/10" data-id="element-2003">
                  <Zap size={20} data-id="element-2004" />
                </div>
                <span className="text-sm font-bold" data-id="element-2005">
                  2-Hour Quote Turnaround
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-2006">
                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-[#FFD700] border border-white/10" data-id="element-2007">
                  <FileCheck size={20} data-id="element-2008" />
                </div>
                <span className="text-sm font-bold" data-id="element-2009">DOT Compliance Ready</span>
              </div>
            </div>
          </motion.div>

          <div className="bg-white rounded-[32px] p-10 text-[#002366] shadow-2xl relative" data-id="element-2010">
            <h3 className="text-2xl font-black mb-8" data-id="element-2011">Quick Quote</h3>
            <form className="space-y-6" data-id="element-2012">
              <div className="space-y-2" data-id="element-2013">
                <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-2014">
                  DOT Number
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366] font-bold" placeholder="1234567" data-id="element-2015" />
              </div>
              <div className="space-y-2" data-id="element-2016">
                <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-2017">
                  Fleet Size
                </label>
                <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366] font-bold" data-id="element-2018">
                  <option data-id="element-2019">Owner-Operator (1 Truck)</option>
                  <option data-id="element-2020">Small Fleet (2-10 Trucks)</option>
                  <option data-id="element-2021">Large Fleet (10+ Trucks)</option>
                </select>
              </div>
              <button className="w-full py-5 bg-[#D32F2F] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#B71C1C] transition-all shadow-xl shadow-[#D32F2F]/20" data-id="element-2022">
                Get Quote Now
              </button>
            </form>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#D32F2F]/10 -z-10" data-id="element-2023"></div>
      </section>

      {/* Fleet Types */}
      <section className="py-24 px-8 max-w-7xl mx-auto" data-id="element-2024">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2025">
          {[{
          title: 'Full Fleet',
          icon: Truck,
          desc: 'Comprehensive liability and physical damage for multi-unit operations.'
        }, {
          title: 'Box Trucks',
          icon: MapPin,
          desc: 'Specialized local delivery and last-mile logistics protection.'
        }, {
          title: 'Owner-Operators',
          icon: Shield,
          desc: 'Flexible coverage designed for the independent professional.'
        }].map((item, i) => <div key={i} className="p-10 bg-white/5 rounded-[24px] border border-white/10 hover:border-[#FFD700] transition-all group" data-id="element-2026">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-[#FFD700] mb-8 border border-white/10 group-hover:bg-[#FFD700] group-hover:text-[#002366] transition-all" data-id="element-2027">
                <item.icon size={32} data-id="element-2028" />
              </div>
              <h3 className="text-2xl font-black mb-4" data-id="element-2029">{item.title}</h3>
              <p className="text-white/40 font-medium leading-relaxed mb-6" data-id="element-2030">
                {item.desc}
              </p>
              <div className="flex items-center gap-2 text-[#FFD700] font-bold text-sm" data-id="element-2031">
                <CheckCircle2 size={16} data-id="element-2032" /> Operational Continuity
              </div>
            </div>)}
        </div>
      </section>

      <SimonsFooter data-id="element-2033" />
    </div>;
}